import type { QuestionNode, Answer } from "../engine";
import { announce, focusFirstHeading } from "./a11y";
import { t } from "./i18n";

export function renderQuestion(
  container: HTMLElement,
  question: QuestionNode,
  questionNumber: number,
  onAnswer: (answer: Answer) => void,
  onBack: (() => void) | null,
  previousAnswer?: Answer
): void {
  while (container.firstChild) container.removeChild(container.firstChild);

  const wrapper = document.createElement("div");
  wrapper.className = "questionnaire";

  const progressText = t("ui.question.progress", { value: questionNumber });

  const progress = document.createElement("p");
  progress.className = "progress-indicator";
  progress.textContent = progressText;
  wrapper.appendChild(progress);

  const questionText = document.createElement("h2");
  questionText.className = "question-text";
  questionText.textContent = question.text;
  wrapper.appendChild(questionText);

  if (question.helpText) {
    const help = document.createElement("p");
    help.className = "help-text";
    help.textContent = question.helpText;
    wrapper.appendChild(help);
  }

  const form = document.createElement("fieldset");
  form.className = "answer-fieldset";
  const legend = document.createElement("legend");
  legend.className = "sr-only";
  legend.textContent = question.text;
  form.appendChild(legend);

  if (question.inputType === "single-select") {
    form.setAttribute("role", "radiogroup");
    form.setAttribute("aria-label", question.text);
  }

  switch (question.inputType) {
    case "boolean":
      appendBooleanInput(form, onAnswer, previousAnswer);
      break;
    case "single-select":
      appendSingleSelectInput(form, question, onAnswer, previousAnswer);
      break;
    case "multi-select":
      appendMultiSelectInput(form, question, onAnswer, previousAnswer);
      break;
    case "numeric":
      appendNumericInput(form, onAnswer, previousAnswer);
      break;
  }

  wrapper.appendChild(form);

  if (onBack) {
    const backBtn = document.createElement("button");
    backBtn.type = "button";
    backBtn.className = "btn-secondary";
    backBtn.textContent = t("ui.nav.back");
    backBtn.addEventListener("click", onBack);
    wrapper.appendChild(backBtn);
  }

  container.appendChild(wrapper);
  focusFirstHeading(wrapper);
  announce(`${progressText}. ${question.text}`);
}

function appendBooleanInput(
  form: HTMLFieldSetElement,
  onAnswer: (answer: Answer) => void,
  previousAnswer?: Answer
): void {
  const btnGroup = document.createElement("div");
  btnGroup.className = "btn-group";

  const yesBtn = document.createElement("button");
  yesBtn.type = "button";
  yesBtn.className = "btn-option";
  if (previousAnswer === "yes") yesBtn.classList.add("btn-option-selected");
  yesBtn.textContent = t("ui.answer.yes");
  yesBtn.addEventListener("click", () => onAnswer("yes"));
  btnGroup.appendChild(yesBtn);

  const noBtn = document.createElement("button");
  noBtn.type = "button";
  noBtn.className = "btn-option";
  if (previousAnswer === "no") noBtn.classList.add("btn-option-selected");
  noBtn.textContent = t("ui.answer.no");
  noBtn.addEventListener("click", () => onAnswer("no"));
  btnGroup.appendChild(noBtn);

  form.appendChild(btnGroup);
}

function appendSingleSelectInput(
  form: HTMLFieldSetElement,
  question: QuestionNode,
  onAnswer: (answer: Answer) => void,
  previousAnswer?: Answer
): void {
  const options = question.options ?? [];
  const prevVal = previousAnswer !== undefined ? String(previousAnswer) : null;
  let selectedValue: string | null = prevVal;

  options.forEach((option) => {
    const label = document.createElement("label");
    label.className = "radio-option";
    if (option.value === prevVal) label.classList.add("radio-option-selected");

    const radio = document.createElement("input");
    radio.type = "radio";
    radio.name = "single-select";
    radio.value = option.value;
    if (option.value === prevVal) radio.checked = true;
    radio.addEventListener("change", () => {
      if (!radio.checked) return;
      selectedValue = option.value;
      form.querySelectorAll(".radio-option").forEach((el) => el.classList.remove("radio-option-selected"));
      label.classList.add("radio-option-selected");
    });

    const span = document.createElement("span");
    span.textContent = option.label;

    label.appendChild(radio);
    label.appendChild(span);
    form.appendChild(label);
  });

  const continueBtn = document.createElement("button");
  continueBtn.type = "button";
  continueBtn.className = "btn-primary";
  continueBtn.textContent = t("ui.nav.continue");
  continueBtn.addEventListener("click", () => {
    if (selectedValue !== null) onAnswer(selectedValue);
  });
  form.appendChild(continueBtn);
}

function appendMultiSelectInput(
  form: HTMLFieldSetElement,
  question: QuestionNode,
  onAnswer: (answer: Answer) => void,
  previousAnswer?: Answer
): void {
  const options = question.options ?? [];
  const prevSelected = Array.isArray(previousAnswer) ? new Set(previousAnswer as string[]) : new Set<string>();
  const selected = new Set<string>(prevSelected);

  const realBoxes: HTMLInputElement[] = [];
  let noneBox: HTMLInputElement | null = null;
  let allBox: HTMLInputElement | null = null;
  const clearCatchAll = () => {
    if (noneBox) noneBox.checked = false;
    if (allBox) allBox.checked = false;
  };

  options.forEach((option) => {
    const label = document.createElement("label");
    label.className = "checkbox-option";

    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.value = option.value;
    if (prevSelected.has(option.value)) checkbox.checked = true;
    checkbox.addEventListener("change", () => {
      if (checkbox.checked) {
        selected.add(option.value);
      } else {
        selected.delete(option.value);
      }
      // Once the person edits individual items, the catch-all controls no longer apply.
      clearCatchAll();
    });
    realBoxes.push(checkbox);

    const span = document.createElement("span");
    span.textContent = option.label;

    label.appendChild(checkbox);
    label.appendChild(span);
    form.appendChild(label);
  });

  // "None of these apply": a universal catch-all that makes an empty answer an explicit,
  // low-friction choice. Selecting it clears every item.
  {
    const label = document.createElement("label");
    label.className = "checkbox-option checkbox-option-catch-all";
    noneBox = document.createElement("input");
    noneBox.type = "checkbox";
    noneBox.dataset.catchAll = "none";
    noneBox.addEventListener("change", () => {
      if (noneBox?.checked) {
        selected.clear();
        realBoxes.forEach((b) => (b.checked = false));
        if (allBox) allBox.checked = false;
      }
    });
    const span = document.createElement("span");
    span.textContent = t("ui.checklist.none_apply");
    label.appendChild(noneBox);
    label.appendChild(span);
    form.appendChild(label);
  }

  // "All of these apply": opt-in per question. Selecting it checks every item.
  if (question.allowAllApply) {
    const label = document.createElement("label");
    label.className = "checkbox-option checkbox-option-catch-all";
    allBox = document.createElement("input");
    allBox.type = "checkbox";
    allBox.dataset.catchAll = "all";
    allBox.addEventListener("change", () => {
      if (allBox?.checked) {
        selected.clear();
        options.forEach((o) => selected.add(o.value));
        realBoxes.forEach((b) => (b.checked = true));
        if (noneBox) noneBox.checked = false;
      } else {
        selected.clear();
        realBoxes.forEach((b) => (b.checked = false));
      }
    });
    const span = document.createElement("span");
    span.textContent = t("ui.checklist.all_apply");
    label.appendChild(allBox);
    label.appendChild(span);
    form.appendChild(label);
  }

  const continueBtn = document.createElement("button");
  continueBtn.type = "button";
  continueBtn.className = "btn-primary";
  continueBtn.textContent = t("ui.nav.continue");
  continueBtn.addEventListener("click", () => {
    onAnswer(Array.from(selected));
  });
  form.appendChild(continueBtn);
}

function appendNumericInput(
  form: HTMLFieldSetElement,
  onAnswer: (answer: Answer) => void,
  previousAnswer?: Answer
): void {
  const input = document.createElement("input");
  input.type = "number";
  input.className = "numeric-input";
  input.placeholder = t("ui.numeric.placeholder");
  if (typeof previousAnswer === "number") input.value = String(previousAnswer);
  form.appendChild(input);

  const continueBtn = document.createElement("button");
  continueBtn.type = "button";
  continueBtn.className = "btn-primary";
  continueBtn.textContent = t("ui.nav.continue");
  continueBtn.addEventListener("click", () => {
    const value = input.value.trim();
    const parsed = parseFloat(value);
    if (value !== "" && !isNaN(parsed)) {
      onAnswer(parsed);
    } else {
      input.setCustomValidity(t("ui.numeric.invalid"));
      input.reportValidity();
    }
  });
  form.appendChild(continueBtn);
}
